import type { PatternTool } from '@miyuki/domain';
import type { PaletteDto } from '@miyuki/types';
import { cn } from '@miyuki/ui';
import { EraserIcon, PlusIcon } from 'lucide-react';

import { t } from '@/lib/i18n';

/**
 * Franja de colores del diseño.
 *
 * Los colores se identifican solo por su muestra: sin nombres ni etiquetas
 * (RF-14). El borrador se selecciona igual que un color, de modo que pintar y
 * borrar son el mismo gesto con distinta herramienta activa (RF-23).
 */
export interface PaletteStripProps {
  readonly palette: PaletteDto;
  readonly tool: PatternTool;
  readonly onSelect: (tool: PatternTool) => void;
  readonly onEdit: (index: number) => void;
  readonly onAdd: () => void;
}

export function PaletteStrip({ palette, tool, onSelect, onEdit, onAdd }: PaletteStripProps) {
  return (
    <div
      className="flex items-center gap-2 overflow-x-auto px-3 py-2"
      role="radiogroup"
      aria-label={t('palette')}
    >
      {palette.colors.map((color, index) => {
        const active = tool.kind === 'color' && tool.index === index;

        return (
          <button
            key={`${color}-${index}`}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={`${t('palette')} ${index + 1}`}
            className={cn(
              'size-11 shrink-0 rounded-full border-2 transition-transform',
              active ? 'border-foreground scale-110' : 'border-border',
            )}
            style={{ backgroundColor: color }}
            onClick={() => {
              // Tocar el color activo lo abre para editarlo: un gesto menos
              // que ir a buscar un botón aparte.
              if (active) onEdit(index);
              else onSelect({ kind: 'color', index });
            }}
          />
        );
      })}

      {/* El "+" nunca desaparece: alcanzar el tope declarado se resuelve
          preguntando (RF-07), y alcanzar los 99 informándolo (RF-09). */}
      {
        <button
          type="button"
          aria-label={t('addColor')}
          className="border-border text-muted-foreground flex size-11 shrink-0 items-center justify-center rounded-full border-2 border-dashed"
          onClick={onAdd}
        >
          <PlusIcon className="size-4" />
        </button>
      }

      <div className="bg-border mx-1 h-8 w-px shrink-0" />

      <button
        type="button"
        role="radio"
        aria-checked={tool.kind === 'eraser'}
        aria-label={t('eraser')}
        className={cn(
          'flex size-11 shrink-0 items-center justify-center rounded-full border-2',
          tool.kind === 'eraser'
            ? 'border-foreground bg-accent scale-110'
            : 'border-border bg-card',
        )}
        onClick={() => {
          onSelect({ kind: 'eraser' });
        }}
      >
        <EraserIcon className="size-4" />
      </button>
    </div>
  );
}
